/**
 * MatchStatusFeedbackModal — fires when an operator clicks
 * Mark Pursuing / Mark Passed / Mark Introduced on the match detail
 * page. Captures structured reasons + a free-form note before the
 * status change is applied.
 *
 * The structured options are intentionally short and pre-built per
 * status; they're stamped on Match.status_feedback_reasons (JSON list)
 * and Match.status_feedback_note (text). The matching engine reads
 * these on its next re-score pass to bias future matches.
 */
import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { MatchStatus } from '../types/match';


interface Props {
  open: boolean;
  newStatus: MatchStatus;
  onClose: () => void;
  onSubmit: (reasons: string[], note: string) => Promise<void>;
}


// Reason taxonomies per status. Pass options are about why the match
// didn't fit; pursue/introduce options are about why it did. Keep the
// taxonomy small — too many options drives skip-and-submit-empty UX.
const PASS_REASONS: { value: string; label: string }[] = [
  { value: 'wrong_stage',           label: 'Wrong stage' },
  { value: 'wrong_sector',          label: 'Wrong sector' },
  { value: 'check_size_mismatch',   label: 'Check size mismatch' },
  { value: 'valuation_too_high',    label: 'Valuation too high' },
  { value: 'team_concerns',         label: 'Team concerns' },
  { value: 'not_currently_raising', label: 'Not currently raising' },
  { value: 'poor_traction',         label: 'Poor traction' },
  { value: 'pitch_quality',         label: 'Weak pitch / materials' },
  { value: 'geographic_mismatch',   label: 'Geographic mismatch' },
  { value: 'already_passed',        label: 'Previously passed' },
];

const PURSUE_REASONS: { value: string; label: string }[] = [
  { value: 'strong_overall_fit',    label: 'Strong overall fit' },
  { value: 'stage_match',           label: 'Stage match' },
  { value: 'sector_match',          label: 'Sector match' },
  { value: 'founder_fit',           label: 'Founder fit' },
  { value: 'traction_validated',    label: 'Traction validated' },
  { value: 'thesis_alignment',      label: 'Thesis alignment' },
  { value: 'co_investor_signal',    label: 'Strong co-investor signal' },
  { value: 'wants_diligence',       label: 'Wants diligence' },
];

const INTRODUCE_REASONS: { value: string; label: string }[] = [
  { value: 'warm_intro_made',       label: 'Warm intro made' },
  { value: 'investor_responded',    label: 'Investor responded' },
  { value: 'meeting_scheduled',     label: 'Meeting scheduled' },
];


const titleFor = (status: MatchStatus): string => {
  if (status === 'passed') return 'Why are you passing on this match?';
  if (status === 'pursuing') return 'Why are you pursuing this match?';
  if (status === 'introduced') return 'Confirm the introduction';
  if (status === 'reviewed') return 'Note your review';
  return 'Status feedback';
};

const subTitleFor = (status: MatchStatus): string => {
  if (status === 'passed') {
    return 'Your reasons feed back into the matching engine so similar startups score lower for this investor in future passes.';
  }
  if (status === 'pursuing') {
    return 'Tells the matching engine which dimensions actually matter for this investor. Future similar matches score higher.';
  }
  if (status === 'introduced') {
    return 'Optional notes about how the introduction went — used for outcome tracking.';
  }
  return 'Quick notes about this match, optional.';
};

const optionsFor = (status: MatchStatus) => {
  if (status === 'passed') return PASS_REASONS;
  if (status === 'pursuing') return PURSUE_REASONS;
  if (status === 'introduced') return INTRODUCE_REASONS;
  return [];
};

const colorFor = (status: MatchStatus): string => {
  if (status === 'passed') return 'red';
  if (status === 'pursuing') return 'blue';
  if (status === 'introduced') return 'purple';
  return 'amber';
};


const MatchStatusFeedbackModal: React.FC<Props> = ({
  open, newStatus, onClose, onSubmit,
}) => {
  const [reasons, setReasons] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setReasons(new Set());
      setNote('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const opts = optionsFor(newStatus);
  const color = colorFor(newStatus);
  const isPassive = opts.length === 0;  // 'reviewed' has no chips

  const toggle = (v: string) => {
    setReasons((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(Array.from(reasons), note.trim());
      // Parent closes on success.
    } finally {
      setSubmitting(false);
    }
  };

  // Skip-feedback path — fires the status change without any reasons.
  // Useful for cases like "reviewed" where structured feedback is
  // optional, or when the operator just wants to advance.
  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await onSubmit([], '');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {titleFor(newStatus)}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{subTitleFor(newStatus)}</p>
          </div>
          <button
            onClick={() => !submitting && onClose()}
            className="p-1 text-gray-400 hover:text-gray-700"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {!isPassive && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Reasons (pick any that apply)
              </label>
              <div className="flex flex-wrap gap-2">
                {opts.map((o) => {
                  const active = reasons.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggle(o.value)}
                      disabled={submitting}
                      className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                        active
                          ? `border-${color}-400 bg-${color}-50 text-${color}-800`
                          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {active ? '✓ ' : ''}{o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Note {isPassive ? '' : '(optional)'}
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                newStatus === 'passed'
                  ? 'Anything specific about this deal? (e.g. saw it last cycle, founder pivoted)'
                  : newStatus === 'pursuing'
                  ? 'What is the next step? (e.g. DD next week, focus on unit economics)'
                  : 'Any notes for the team...'
              }
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-white rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleSkip}
              disabled={submitting}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-white rounded-lg disabled:opacity-50"
              title="Apply the status change without leaving feedback"
            >
              Skip feedback
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${
                color === 'red'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : color === 'purple'
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save & change status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchStatusFeedbackModal;
